import { getAdminToken } from '../api'

export type HarnessRoundLog = {
  round: number
  max_rounds?: number
  retrieval_top_k?: number
  hit_count?: number
  engine?: string
  titles?: string[]
  found?: boolean
  refs_count?: number
  action?: string
  status: 'searching' | 'retrieved' | 'thinking' | 'judging' | 'done'
}

export type ChatStreamEvent =
  | { type: 'user_saved'; message: Record<string, unknown> }
  | {
      type: 'harness_round_start'
      round: number
      max_rounds: number
      /** 与后台 rag_retrieval_top_k 一致：每轮向 ES 请求的切片条数 */
      retrieval_top_k: number
      base_top_k?: number
      page_size?: number
    }
  | {
      type: 'harness_round_retrieval'
      round: number
      max_rounds: number
      retrieval_top_k: number
      page_size?: number
      hit_count: number
      page_hit_count?: number
      new_hits_this_round?: number
      agent_search?: boolean
      engine?: string
      titles?: string[]
      merged_titles?: string[]
      preview_refs?: { item_id: number; title?: string }[]
      retrieval_query?: string
      rewritten?: boolean
      entity_scope?: {
        action?: string
        note?: string
        matched_entities?: string[]
        item_ids?: number[]
        filter_locked?: boolean
        graph_items_count?: number
        entity_pool_size?: number
      }
    }
  | { type: 'harness_think_start'; round: number; max_rounds: number }
  | { type: 'harness_think_end'; round: number; max_rounds: number }
  | { type: 'harness_judge_start'; round: number; max_rounds: number; mode?: string }
  | {
      type: 'harness_round_result'
      round: number
      max_rounds: number
      found: boolean
      hit_count?: number
      refs_count?: number
      action?: string
    }
  | {
      type: 'query_rewrite'
      intent?: string
      context_summary?: string
      keywords?: string[]
      queries?: string[]
      skipped?: boolean
      skip_reason?: string
    }
  | { type: 'agent_step_start'; step: number; max_steps: number }
  | { type: 'agent_reasoning_start'; step: number }
  | { type: 'agent_reasoning_delta'; step: number; content: string }
  | { type: 'agent_reasoning_end'; step: number }
  | {
      type: 'agent_tool_call'
      step: number
      max_steps: number
      tool: string
      reasoning?: string
      arguments?: Record<string, unknown>
      plan_ms?: number
    }
  | {
      type: 'agent_sub_invoke'
      step: number
      agent_code?: string
      agent_name?: string
      task?: string
      result_preview?: string
    }
  | {
      type: 'hit_curation_start'
      round: number
      candidate_count?: number
      candidate_docs?: { item_id: number; title: string }[]
    }
  | {
      type: 'hit_curation_done'
      round: number
      skipped?: boolean
      skip_reason?: string
      has_single_primary_doc?: boolean
      answer_shape?: string
      overview_note?: string
      related_docs?: { item_id: number; title: string; relevance_note?: string }[]
      excluded_docs?: { item_id: number; title: string; reason?: string }[]
      ms?: number
    }
  | {
      type: 'entity_scope'
      setting_enabled?: boolean
      action?: string
      note?: string
      matched_entities?: string[]
      item_ids?: number[]
      filter_locked?: boolean
      graph_items_count?: number
      entity_pool_size?: number
      question_preview?: string
    }
  | { type: 'thinking_start'; purpose?: string }
  | { type: 'thinking_delta'; content: string }
  | { type: 'thinking_end' }
  | { type: 'thinking'; content: string }
  | { type: 'delta'; content: string }
  | { type: 'answer_reset'; content: string }
  | { type: 'answer_generation_start'; hit_count?: number; mode?: string }
  | { type: 'answer_generation_progress'; phase?: string }
  | {
      type: 'rag_done'
      found?: boolean
      refs?: { item_id: number; title?: string; scope?: string }[]
      citation_refs?: {
        cite_index?: number
        source_type?: 'kb' | 'web'
        item_id?: number
        title?: string
        chunk_index?: number
        snippet?: string
        url?: string
      }[]
    }
  | { type: 'done'; message: Record<string, unknown>; rag_debug?: unknown }
  | { type: 'error'; message: string }

export async function streamChatMessage(
  convId: number,
  body: Record<string, unknown>,
  onEvent: (ev: ChatStreamEvent) => void,
  signal?: AbortSignal,
): Promise<void> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' }
  const token = getAdminToken()
  if (token) headers.Authorization = `Bearer ${token}`

  const res = await fetch(`/api/public/chat/conversations/${convId}/messages/stream`, {
    method: 'POST',
    headers,
    body: JSON.stringify(body),
    signal,
  })
  if (!res.ok) {
    let msg = res.statusText
    try {
      const j = (await res.json()) as { detail?: string }
      if (typeof j.detail === 'string') msg = j.detail
    } catch {
      /* ignore */
    }
    throw new Error(msg || '请求失败')
  }
  const reader = res.body?.getReader()
  if (!reader) throw new Error('浏览器不支持流式响应')

  const decoder = new TextDecoder()
  let buf = ''
  while (true) {
    const { done, value } = await reader.read()
    if (done) break
    buf += decoder.decode(value, { stream: true })
    const parts = buf.split('\n\n')
    buf = parts.pop() ?? ''
    for (const block of parts) {
      const line = block.trim()
      if (!line.startsWith('data:')) continue
      const raw = line.slice(5).trim()
      if (!raw) continue
      try {
        onEvent(JSON.parse(raw) as ChatStreamEvent)
      } catch {
        /* skip malformed chunk */
      }
    }
  }
}
